import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, } from 'react';
import { useKey } from 'react-use';
import { SkipLinkContext } from '../SkipLinkContext';
export const FocusZoneContext = createContext({
    register: () => () => { },
    unregister: () => { },
    focusAdjacentObject: () => { },
});
export function useRegisterFocus(ref) {
    const { register, unregister } = useContext(FocusZoneContext);
    useEffect(() => {
        if (ref.current === null) {
            return;
        }
        register(ref);
        return () => {
            unregister(ref);
        };
    }, [ref, register, unregister]);
}
export const FocusZone = ({ direction, children, notCyclical = false, _type, singleDirected = false, }) => {
    const nextElement = direction === 'vertical' ? 'ArrowDown' : 'ArrowRight';
    const previousElement = direction === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
    const nextInParent = direction === 'vertical' ? 'ArrowRight' : 'ArrowDown';
    const previousInParent = direction === 'vertical' ? 'ArrowLeft' : 'ArrowUp';
    const { register: parentRegister, unregister: parentUnregister, focusAdjacentObject: parentFocusAdjacentObject, } = useContext(FocusZoneContext);
    const focusElements = useRef([]);
    const { registerFocusObject, unregisterFocusObject } = useContext(SkipLinkContext);
    //register in parent FocusZone
    useEffect(() => {
        const focusElement = { getRefs: () => focusElements.current };
        parentRegister(focusElement);
        return () => {
            parentUnregister(focusElement);
        };
    }, [focusElements, parentRegister, parentUnregister]);
    const register = useCallback((focusElement) => {
        if (_type && focusElements.current.length < 1) {
            if ('current' in focusElement) {
                registerFocusObject(_type, focusElement);
            }
        }
        const insertIndex = focusElements.current.findIndex((element) => {
            return !isElementSuccesser(focusElement, element);
        });
        if (insertIndex === -1) {
            focusElements.current.push(focusElement);
        }
        else {
            focusElements.current.splice(insertIndex, 0, focusElement);
        }
    }, [_type, registerFocusObject]);
    const unregister = useCallback((ref) => {
        if (_type && focusElements.current.length === 1) {
            unregisterFocusObject(_type);
        }
        focusElements.current = focusElements.current.filter((element) => {
            if ('current' in element && 'current' in ref) {
                return (element === null || element === void 0 ? void 0 : element.current) !== ref.current;
            }
            else {
                return element !== ref;
            }
        });
    }, [unregisterFocusObject, _type]);
    /**
     * focuses nearest adjacent object to given object
     * @param next detemins if next or previos object should be focused
     * @param ref currently focused object
     */
    const focusAdjacentObject = useCallback((next, ref) => {
        const length = focusElements.current.length;
        const index = findIndexOfRef(ref, focusElements.current);
        if (index === -1) {
            return;
        }
        let newIndex = index;
        let elementToFocus;
        do {
            newIndex = next
                ? incrementIndex(newIndex, length, notCyclical)
                : decrementIndex(newIndex, length, notCyclical);
            elementToFocus = findNearestFocussableElement(ref, focusElements.current[newIndex]);
        } while (elementToFocus === undefined && newIndex !== index && newIndex !== -1);
        elementToFocus === null || elementToFocus === void 0 ? void 0 : elementToFocus.current.focus();
        if (elementToFocus === undefined) {
            parentFocusAdjacentObject(next, ref);
        }
    }, [parentFocusAdjacentObject, notCyclical]);
    const context = useMemo(() => ({
        register,
        unregister,
        focusAdjacentObject,
    }), [register, unregister, focusAdjacentObject]);
    useKey((e) => e.key === nextElement ||
        e.key === previousElement ||
        ((e.key === nextInParent || e.key === previousInParent) && !singleDirected), (e) => {
        const currentFocusElement = findFocusedElement(focusElements.current);
        if (currentFocusElement === undefined) {
            return;
        }
        e.preventDefault();
        if ('current' in currentFocusElement) {
            if (e.key === nextElement || e.key === previousElement) {
                focusAdjacentObject(e.key === nextElement, currentFocusElement);
            }
            if (e.key === nextInParent || e.key === previousInParent) {
                parentFocusAdjacentObject(e.key === nextInParent, currentFocusElement);
                e.stopImmediatePropagation();
            }
        }
    });
    return _jsx(FocusZoneContext.Provider, Object.assign({ value: context }, { children: children }), void 0);
};
/**
 * returns focused element if it is contained by 'focusElements'
 * @param focusElements arry to search in
 */
function findFocusedElement(focusElements) {
    return focusElements.find((array) => {
        if ('current' in array) {
            return document.activeElement === array.current;
        }
        return false;
    });
}
/**
 * Returns if element is focusable
 * @param element HTML-element to check
 */
function isElementFocusable(element) {
    var _a, _b, _c;
    if (element === undefined) {
        return false;
    }
    return ((_b = (_a = element === null || element === void 0 ? void 0 : element.current) === null || _a === void 0 ? void 0 : _a.style) === null || _b === void 0 ? void 0 : _b.display) !== 'none' && ((_c = element === null || element === void 0 ? void 0 : element.current) === null || _c === void 0 ? void 0 : _c.disabled) !== true;
}
/**
 * returns incremented index or 0 if index === range - 1 and notCyclical === false
 * @param index index to be incremented
 * @param range number that the index must not reach or exeed
 * @param notCyclical if true and max index would be exeeded, -1 is returned
 */
function incrementIndex(index, range, notCyclical) {
    if (index === range - 1 && notCyclical) {
        return -1;
    }
    return (index + 1) % range;
}
/**
 * returns decremented index or range - 1 if index === 0 and notCyclical === false
 * @param index index to be decrementes
 * @param range number that the index must not reach or exeed
 * @param notCyclical if true and index would fall below 0, -1 is returned
 */
function decrementIndex(index, range, notCyclical) {
    if (index === 0) {
        if (notCyclical) {
            return -1;
        }
        return range - 1;
    }
    return index - 1;
}
/**
 * returns index of FocusElement that holds ref
 * @param ref ref that is searched for
 * @param array array to be searched in
 */
function findIndexOfRef(ref, array) {
    return array.findIndex((element) => {
        if ('current' in element) {
            return element.current === ref.current;
        }
        else {
            return findIndexOfRef(ref, element.getRefs()) !== -1;
        }
    });
}
/**
 * returns nearest element to ref in focusElement
 * @param ref comparison element
 * @param focusElement object to search in
 * @param distance distance to must be fallen below
 */
function findNearestFocussableElement(ref, focusElement, distance) {
    if (focusElement === undefined) {
        return undefined;
    }
    let result = undefined;
    let minDistance = distance;
    if ('current' in focusElement && isElementFocusable(focusElement)) {
        const newDistance = calculateDistance(focusElement, ref);
        if (newDistance !== undefined && (minDistance === undefined || newDistance < minDistance)) {
            minDistance = newDistance;
            result = focusElement;
        }
    }
    if ('getRefs' in focusElement) {
        focusElement.getRefs().forEach((element) => {
            const newResult = findNearestFocussableElement(ref, element, minDistance);
            if (newResult !== undefined && newResult !== null) {
                minDistance = calculateDistance(newResult, ref);
                result = newResult;
            }
        });
    }
    return result;
}
/**
 * returns distance betwenn two refs
 * @param ref1 first ref
 * @param ref2 second ref
 */
function calculateDistance(ref1, ref2) {
    if (ref1.current === null ||
        ref2.current === null ||
        ref1.current === undefined ||
        ref2.current === undefined) {
        return undefined;
    }
    const rect1 = ref1.current.getBoundingClientRect();
    const rect2 = ref2.current.getBoundingClientRect();
    const distance = Math.sqrt(Math.pow((rect1.left + rect1.right) / 2 - (rect2.left + rect2.right) / 2, 2) +
        Math.pow((rect1.top + rect1.bottom) / 2 - (rect2.top + rect2.bottom) / 2, 2));
    return distance;
}
/**
 * returns if element1 succeeds element2
 * @param element1
 * @param element2
 */
function isElementSuccesser(element1, element2) {
    var _a;
    const first = findFirstRef(element1);
    const last = findLastRef(element2);
    if (first === undefined || last === undefined) {
        return undefined;
    }
    return (((_a = last === null || last === void 0 ? void 0 : last.current) === null || _a === void 0 ? void 0 : _a.compareDocumentPosition(first === null || first === void 0 ? void 0 : first.current)) === Node.DOCUMENT_POSITION_FOLLOWING);
}
/**
 * returns first ref in tree of element
 * @param element
 */
function findFirstRef(element) {
    if (element === undefined) {
        return undefined;
    }
    if ('current' in element) {
        return element;
    }
    else {
        return findFirstRef(element.getRefs()[0]);
    }
}
/**
 * returns last ref in tree of element
 * @param element
 */
function findLastRef(element) {
    if (element === undefined) {
        return undefined;
    }
    if ('current' in element) {
        return element;
    }
    else {
        const refs = element.getRefs();
        return findLastRef(refs[refs.length - 1]);
    }
}
//# sourceMappingURL=FocusZone.js.map